/**
 * AGI-428: the pure display rules for the Meta-pricing notice.
 *
 * 🔴 NOTHING HERE COMPUTES AN ESTIMATE. The seeding command already converted
 * the 28-day USD reading into a monthly figure in the workspace's billing
 * currency (MXN integer with IVA, USD with two decimals). These functions only
 * decide how that number is written down and which copy keys it sits next to,
 * which is the whole reason the front can be wrong about presentation without
 * ever being wrong about money.
 */

import type { tAlertSourceState } from './AlertCenter.types';
import {
  getMetaPricingMessage,
  type tMetaPricingCopyVariant,
  type tMetaPricingCurrency,
  type tMetaPricingMessage,
  type tMetaPricingNotice,
  type tMetaPricingPayload,
  type tMetaPricingUrgency,
} from './MetaPricingNotice.types';

/**
 * The three CTAs Growth agreed on (hilo #growth-marketing, 15-sep; urgency
 * media became a Calendly booking on 18-sep). The URGENCY picks the label; the
 * payload carries the URL, so moving a link never needs a deploy. Both halves
 * have to stay in step: a "Reserva tu cupo para el webinar en vivo" that opens
 * a help article is worse than no button. Every CTA works at any hour: alta
 * books a 1:1 slot, media books a seat, baja opens a help article, so no CTA
 * carries opening hours (Ariel, 18-sep; the Meet room and its hours are gone).
 */
export const META_PRICING_CTA_LABEL_KEYS: Record<tMetaPricingUrgency, string> =
  {
    alta: 'metaPricing.cta.alta',
    media: 'metaPricing.cta.media',
    baja: 'metaPricing.cta.baja',
  };

/**
 * The paragraphs each copy variant renders, in order. Absent entries render
 * nothing at all rather than an empty paragraph: the prototype hides `p3`/`p4`
 * for three of the five variants, and an empty `<p>` still costs a gap.
 */
export const META_PRICING_PARAGRAPH_KEYS: Record<
  tMetaPricingCopyVariant,
  string[]
> = {
  sube_extremo: [
    'metaPricing.copy.subeExtremo.p1',
    'metaPricing.copy.subeExtremo.p2',
    'metaPricing.copy.subeExtremo.p3',
    'metaPricing.copy.subeExtremo.p4',
  ],
  cuenta_clave: [
    'metaPricing.copy.cuentaClave.p1',
    'metaPricing.copy.cuentaClave.p2',
    'metaPricing.copy.cuentaClave.p3',
  ],
  cobro_nuevo: [
    'metaPricing.copy.cobroNuevo.p1',
    'metaPricing.copy.cobroNuevo.p2',
  ],
  sube_moderado: [
    'metaPricing.copy.subeModerado.p1',
    'metaPricing.copy.subeModerado.p2',
  ],
  baja_urgencia: [
    'metaPricing.copy.bajaUrgencia.p1',
    'metaPricing.copy.bajaUrgencia.p2',
    'metaPricing.copy.bajaUrgencia.p3',
  ],
};

/** The closing question, and the closing line when the variant has one. */
export const META_PRICING_CLOSING_KEYS: Record<
  tMetaPricingCopyVariant,
  { question: string; sign?: string }
> = {
  sube_extremo: {
    question: 'metaPricing.copy.subeExtremo.question',
    sign: 'metaPricing.copy.subeExtremo.closing',
  },
  cuenta_clave: {
    question: 'metaPricing.copy.cuentaClave.question',
    sign: 'metaPricing.copy.cuentaClave.closing',
  },
  cobro_nuevo: { question: 'metaPricing.copy.cobroNuevo.question' },
  sube_moderado: { question: 'metaPricing.copy.subeModerado.question' },
  baja_urgencia: {
    question: 'metaPricing.copy.bajaUrgencia.question',
    sign: 'metaPricing.copy.bajaUrgencia.closing',
  },
};

/**
 * AGI-428, message 2 (25-sep): what each copy variant says around the amounts
 * card. `lead` goes BEFORE the card, `videoIntro` between the card and the
 * player, then the question and, when the variant has one, the sign-off.
 *
 * The same five variants as message 1 on purpose: every workspace gets the
 * follow-up of the message it already read, resolved by the same rule
 * (a key account gets its own copy unless it stays at 0, then baja). Copy is
 * Fanny's v4, verbatim.
 */
export type tMetaPricingTipsCopyKeys = {
  lead: string[];
  videoIntro: string;
  question: string;
  sign?: string;
};

export const META_PRICING_TIPS_COPY_KEYS: Record<
  tMetaPricingCopyVariant,
  tMetaPricingTipsCopyKeys
> = {
  sube_extremo: {
    lead: [
      'metaPricing.tips.copy.subeExtremo.p1',
      'metaPricing.tips.copy.subeExtremo.p2',
    ],
    videoIntro: 'metaPricing.tips.copy.subeExtremo.videoIntro',
    question: 'metaPricing.tips.copy.subeExtremo.question',
    sign: 'metaPricing.tips.copy.subeExtremo.closing',
  },
  cuenta_clave: {
    lead: [
      'metaPricing.tips.copy.cuentaClave.p1',
      'metaPricing.tips.copy.cuentaClave.p2',
    ],
    videoIntro: 'metaPricing.tips.copy.cuentaClave.videoIntro',
    question: 'metaPricing.tips.copy.cuentaClave.question',
    sign: 'metaPricing.tips.copy.cuentaClave.closing',
  },
  cobro_nuevo: {
    lead: [
      'metaPricing.tips.copy.cobroNuevo.p1',
      'metaPricing.tips.copy.cobroNuevo.p2',
    ],
    videoIntro: 'metaPricing.tips.copy.cobroNuevo.videoIntro',
    question: 'metaPricing.tips.copy.cobroNuevo.question',
  },
  sube_moderado: {
    lead: [
      'metaPricing.tips.copy.subeModerado.p1',
      'metaPricing.tips.copy.subeModerado.p2',
    ],
    videoIntro: 'metaPricing.tips.copy.subeModerado.videoIntro',
    question: 'metaPricing.tips.copy.subeModerado.question',
  },
  baja_urgencia: {
    lead: ['metaPricing.tips.copy.bajaUrgencia.p1'],
    videoIntro: 'metaPricing.tips.copy.bajaUrgencia.videoIntro',
    question: 'metaPricing.tips.copy.bajaUrgencia.question',
    sign: 'metaPricing.tips.copy.bajaUrgencia.closing',
  },
};

/**
 * AGI-428, message 3 (6-oct): what each copy variant says around the free
 * window card. `lead` goes BEFORE the card, `windowNote` inside it, under the
 * two rows, `videoIntro` between the card and the player, then the question
 * and the sign-off when the variant has them.
 *
 * The same five variants again, resolved by the same rule as messages 1 and
 * 2. Copy is Growth's mockup verbatim (the "Mensaje 3: Nuevo saldo" tab,
 * #growth-marketing 1-oct), except the greeting, which the modal prints on
 * its own line as it does for the other two messages. The low urgency copy
 * has no note and no question in the mockup, so it has none here.
 */
export type tMetaPricingBalanceCopyKeys = {
  lead: string[];
  windowNote?: string;
  videoIntro: string;
  question?: string;
  sign?: string;
};

export const META_PRICING_BALANCE_COPY_KEYS: Record<
  tMetaPricingCopyVariant,
  tMetaPricingBalanceCopyKeys
> = {
  sube_extremo: {
    lead: [
      'metaPricing.balance.copy.subeExtremo.p1',
      'metaPricing.balance.copy.subeExtremo.p2',
    ],
    windowNote: 'metaPricing.balance.copy.subeExtremo.windowNote',
    videoIntro: 'metaPricing.balance.copy.subeExtremo.videoIntro',
    question: 'metaPricing.balance.copy.subeExtremo.question',
    sign: 'metaPricing.balance.copy.subeExtremo.closing',
  },
  cuenta_clave: {
    lead: [
      'metaPricing.balance.copy.cuentaClave.p1',
      'metaPricing.balance.copy.cuentaClave.p2',
    ],
    windowNote: 'metaPricing.balance.copy.cuentaClave.windowNote',
    videoIntro: 'metaPricing.balance.copy.cuentaClave.videoIntro',
    question: 'metaPricing.balance.copy.cuentaClave.question',
    sign: 'metaPricing.balance.copy.cuentaClave.closing',
  },
  cobro_nuevo: {
    lead: [
      'metaPricing.balance.copy.cobroNuevo.p1',
      'metaPricing.balance.copy.cobroNuevo.p2',
    ],
    windowNote: 'metaPricing.balance.copy.cobroNuevo.windowNote',
    videoIntro: 'metaPricing.balance.copy.cobroNuevo.videoIntro',
    question: 'metaPricing.balance.copy.cobroNuevo.question',
  },
  sube_moderado: {
    lead: [
      'metaPricing.balance.copy.subeModerado.p1',
      'metaPricing.balance.copy.subeModerado.p2',
    ],
    windowNote: 'metaPricing.balance.copy.subeModerado.windowNote',
    videoIntro: 'metaPricing.balance.copy.subeModerado.videoIntro',
    question: 'metaPricing.balance.copy.subeModerado.question',
  },
  baja_urgencia: {
    lead: [
      'metaPricing.balance.copy.bajaUrgencia.p1',
      'metaPricing.balance.copy.bajaUrgencia.p2',
    ],
    videoIntro: 'metaPricing.balance.copy.bajaUrgencia.videoIntro',
    sign: 'metaPricing.balance.copy.bajaUrgencia.closing',
  },
};

/**
 * The i18n prefix of the player's caption, play label and fallback link, per
 * message that carries a video. Message 1 has no player (its urgency media
 * card is a link), so it has no entry.
 */
export const META_PRICING_VIDEO_COPY_PREFIXES: Record<
  Exclude<tMetaPricingMessage, 'impact'>,
  string
> = {
  tips: 'metaPricing.tips.video',
  balance: 'metaPricing.balance.video',
};

/**
 * What the workspace's wallet surfaces decided, as `useWalletSurfaceAccess`
 * returns it. Passed in rather than read here so the gate stays a pure
 * function the hook memoizes and the tests drive directly.
 */
export type tMetaPricingWalletAccess = {
  /** The cohort, the plan and WhatsApp API access all said yes. */
  isWalletSurfaceVisible: boolean;
  /** Enough is known to answer: the cohort settled and, when ON, the plan. */
  isWalletRouteDecidable: boolean;
};

/**
 * AGI-428, message 3: the notice source, as THIS workspace may see it.
 *
 * Message 3 says "desde hoy tus creditos se muestran como saldo", so it only
 * reaches a workspace that actually sees the balance, decided by the SAME
 * predicate that opens the wallet surfaces and the credits-to-balance notice
 * (`useWalletSurfaceAccess`, RET-2325). A Basico or Legacy workspace, or one
 * that lost WhatsApp API since the 779 were listed, would read a sentence its
 * own account contradicts. Ariel, 6-oct: "779, solo si ve el saldo".
 *
 *  - Messages 1 and 2 pass through untouched: they never said anything about
 *    the balance.
 *  - While the wallet answer is not in yet, message 3 is `loading`, never
 *    "absent": R3b holds the family instead of letting it flicker in after
 *    the bell has already counted without it. Only its own family waits.
 *  - A workspace that will not see the balance gets "no notice" (`ready`,
 *    `null`): messages 1 and 2 are expired, so there is nothing to fall back
 *    to, and falling back to an estimate from September would be wrong anyway.
 *
 * Returns the SAME object when nothing is filtered, so the snapshot memo and
 * every consumer of the context keep their reference.
 */
export function gateMetaPricingNoticeOnWallet(
  source: tAlertSourceState<tMetaPricingNotice | null>,
  wallet: tMetaPricingWalletAccess,
): tAlertSourceState<tMetaPricingNotice | null> {
  if (source.status !== 'ready' || source.value === null) return source;
  if (getMetaPricingMessage(source.value.key) !== 'balance') return source;
  if (!wallet.isWalletRouteDecidable) return { status: 'loading', value: null };
  if (!wallet.isWalletSurfaceVisible) return { status: 'ready', value: null };
  return source;
}

export type tMetaPricingVideo =
  | { kind: 'embed'; embedUrl: string; watchUrl: string }
  | { kind: 'link'; watchUrl: string };

const YOUTUBE_ID = /^[A-Za-z0-9_-]{11}$/;
const DRIVE_ID = /^[A-Za-z0-9_-]{10,}$/;

function youtubeIdOf(url: URL, host: string): string | null {
  const segments = url.pathname.split('/').filter(Boolean);
  let id: string | null = null;
  if (host === 'youtu.be') id = segments[0] ?? null;
  else if (host === 'youtube.com' || host === 'youtube-nocookie.com') {
    id =
      url.searchParams.get('v') ??
      (['embed', 'shorts', 'live'].includes(segments[0] ?? '')
        ? (segments[1] ?? null)
        : null);
  }
  return id && YOUTUBE_ID.test(id) ? id : null;
}

function driveIdOf(url: URL, host: string): string | null {
  if (host !== 'drive.google.com') return null;
  const segments = url.pathname.split('/').filter(Boolean);
  const id =
    segments[0] === 'file' && segments[1] === 'd'
      ? (segments[2] ?? null)
      : url.searchParams.get('id');
  return id && DRIVE_ID.test(id) ? id : null;
}

/**
 * AGI-428, message 2: how the payload's `video_url` plays.
 *
 * YouTube and Google Drive play INSIDE the modal (the brief: "dale play y se
 * reproduce ahi mismo"); YouTube is the recommended host, the same embed the
 * account-health explainer already runs in production. Any other https URL
 * degrades to a link that opens in a new tab, and anything that is not an
 * https URL renders nothing.
 *
 * Taken apart with `URL` and rebuilt from a validated id, never passed
 * through: the value arrives from a CSV and a SQL update, and an `iframe src`
 * is not a place for a string nobody parsed.
 */
export function resolveMetaPricingVideo(
  videoUrl: string | null,
): tMetaPricingVideo | null {
  if (!videoUrl) return null;
  let url: URL;
  try {
    url = new URL(videoUrl.trim());
  } catch {
    return null;
  }
  if (url.protocol !== 'https:') return null;
  const host = url.hostname.replace(/^(www|m)\./, '');

  const youtubeId = youtubeIdOf(url, host);
  if (youtubeId) {
    return {
      kind: 'embed',
      embedUrl: `https://www.youtube.com/embed/${youtubeId}?autoplay=1&rel=0`,
      watchUrl: `https://www.youtube.com/watch?v=${youtubeId}`,
    };
  }
  const driveId = driveIdOf(url, host);
  if (driveId) {
    return {
      kind: 'embed',
      embedUrl: `https://drive.google.com/file/d/${driveId}/preview`,
      watchUrl: `https://drive.google.com/file/d/${driveId}/view`,
    };
  }
  return { kind: 'link', watchUrl: url.toString() };
}

/**
 * The waving hand in the greeting. Only the two variants that open with a
 * greeting carry it in the prototype; the other three open on the news, and a
 * wave above "Meta empieza a cobrar" reads as cheerful about it. Messages 2
 * and 3 keep the same two (Fanny's v4 mockup, Growth's message 3 tab).
 */
export function hasMetaPricingWave(variant: tMetaPricingCopyVariant): boolean {
  return variant === 'sube_extremo' || variant === 'baja_urgencia';
}

/**
 * The green "un dato a tu favor" block. Everyone gets it EXCEPT the low
 * urgency copy, whose own paragraphs already say the impact is among the
 * lowest: repeating the good news would be the only thing that page says.
 */
export function hasMetaPricingFavor(variant: tMetaPricingCopyVariant): boolean {
  return variant !== 'baja_urgencia';
}

/**
 * The tools block (video card plus three practices) belongs to urgency MEDIA
 * and to nothing else: alta gets a 1:1 session where a human covers the same
 * ground, and baja has no adaptation to make.
 */
export function hasMetaPricingTools(urgency: tMetaPricingUrgency): boolean {
  return urgency === 'media';
}

/**
 * MXN as `$4,693`, USD as `$169.25`, both with `es-MX` grouping.
 *
 * The decimals are NOT a rounding decision made here: the command already
 * rounded MXN to an integer and USD to two places, so printing MXN with
 * decimals would invent precision the figure does not have, and printing USD
 * without them would hide a number the customer can check against Stripe.
 */
export function formatMetaPricingAmount(
  amount: number,
  currency: tMetaPricingCurrency,
): string {
  const digits = currency === 'MXN' ? 0 : 2;
  return `$${amount.toLocaleString('es-MX', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })}`;
}

/**
 * The label of the "today" row of the amounts card, when it carries a figure.
 *
 * Message 2 says what the figure IS (Ariel, 25-sep). For most of the base the
 * "today" figure is the cost of their current messaging at Meta's list price,
 * which their plan and template credits cover, not what their Leadsales
 * invoice says: "Gasto actual" next to $1,213 read as a bill to a customer who
 * pays $245 (Punto Relieve, 18-sep). Message 1 keeps its label: 779 accounts
 * already read it, and it goes away the moment message 2 is live. Message 3
 * has no amounts card (its card is the free window), so it has no label.
 */
export const META_PRICING_TODAY_LABEL_KEYS: Record<
  Exclude<tMetaPricingMessage, 'balance'>,
  string
> = {
  impact: 'metaPricing.amounts.today',
  tips: 'metaPricing.tips.amounts.today',
};

export type tMetaPricingAmounts = {
  /** `null` when the row should read "hoy no pagas por mensajeria". */
  today: string | null;
  october: string;
  currency: tMetaPricingCurrency;
  /** Whether the MXN "aproximado, con IVA" sentence is due. */
  isApproximate: boolean;
};

/**
 * The amounts card, exactly as the prototype's `amounts()` resolves it.
 *
 * `cobro_nuevo` is the one branch that matters: those workspaces pay nothing
 * for messaging today, so their "gasto actual" row is a SENTENCE, not a
 * figure. Printing `$0` next to `$1,509` reads as a 100% increase from a real
 * baseline; saying "hoy no pagas por mensajeria" says the true thing, which is
 * that the charge is new.
 *
 * The MXN note rides with the currency rather than with the variant: it is a
 * fact about the conversion (USD x 21 + IVA, the wallet rule), so every MXN
 * workspace needs it and no USD workspace does.
 */
export function resolveMetaPricingAmounts(
  payload: tMetaPricingPayload,
): tMetaPricingAmounts {
  return {
    today:
      payload.copyVariant === 'cobro_nuevo'
        ? null
        : formatMetaPricingAmount(payload.amountToday, payload.currency),
    october: formatMetaPricingAmount(payload.amountOctober, payload.currency),
    currency: payload.currency,
    isApproximate: payload.currency === 'MXN',
  };
}
